import type { Node as PMNode, Schema } from '@tiptap/pm/model';
import { Step, StepResult } from '@tiptap/pm/transform';

type TOptionsSnapshot = Record<string, string[] | null>;
type TSerializedStep = { before: TOptionsSnapshot; after: TOptionsSnapshot };

/** Carries ref-backed options through editor history without changing the document's content. */
export class DropdownOptionsStep extends Step {
  constructor(
    readonly before: TOptionsSnapshot,
    readonly after: TOptionsSnapshot
  ) {
    super();
  }

  static create(current: Record<string, string[]>, next: Record<string, string[]>): DropdownOptionsStep {
    const before = Object.fromEntries(
      Object.keys(next).map((fieldId) => [fieldId, current[fieldId] ? [...current[fieldId]] : null])
    );
    const after = Object.fromEntries(Object.entries(next).map(([fieldId, options]) => [fieldId, [...options]]));
    return new DropdownOptionsStep(before, after);
  }

  apply(doc: PMNode): StepResult {
    // Building or filtering a transaction must not mutate the ref. The dropdown plugin writes
    // the options when the transaction is applied, before orphan detection and node rendering.
    // A fresh root also lets autosave distinguish an applied options-only step from a filtered one.
    return StepResult.ok(doc.type.create(doc.attrs, doc.content, doc.marks));
  }

  invert(): DropdownOptionsStep {
    return new DropdownOptionsStep(this.after, this.before);
  }

  map(): DropdownOptionsStep {
    // Options are keyed by fieldId, so document position changes do not affect them.
    return this;
  }

  toJSON() {
    return { stepType: 'dropdownOptions', before: this.before, after: this.after };
  }

  static fromJSON(_schema: Schema, json: TSerializedStep): DropdownOptionsStep {
    return new DropdownOptionsStep(json.before, json.after);
  }
}

Step.jsonID('dropdownOptions', DropdownOptionsStep);
